import { FormTreeData, VisibilityChangeCallback } from './types';
import FormRenderer from './FormRenderer';
import { FormField } from './FormField';
import { SectionStatusBadge } from './SectionStatusBadge';
import { useSubsectionStatus } from '@/hooks/submission_forms/useSubsectionStatus';
import { useRef, useEffect, useState } from 'react';
import { useFormDisclosure } from './ClarificationNavigationContext';
import { useSubmissionFormContext } from './SubmissionFormContext';
import { isFillingInStatus } from './statusUtils';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { useWatch } from 'react-hook-form';

interface FormGroupProps {
  node: FormTreeData;
  depth: number;
  disabled?: boolean;
  onVisibilityChange?: VisibilityChangeCallback;
}

// Consolidated styling utility
const getFormGroupStyles = (depth: number) => {
  const headingTags = ['h2', 'h3', 'h4', 'h5', 'h6', 'h6'] as const;

  const containerStyles = {
    0: 'bg-white rounded-lg shadow-sm border border-gray-200 mb-6',
    1: '',
    default: '',
  };

  const sectionStyles = {
    0: 'scroll-mt-24 p-6',
    1: 'scroll-mt-24',
    default: 'scroll-mt-24 p-4',
  };

  const headingStyles = {
    0: 'font-semibold text-gray-900 mb-4 text-2xl tracking-tight',
    1: 'font-semibold text-gray-900 mb-5 text-xl',
    2: 'font-semibold text-gray-900 mb-4 text-lg',
    default: 'font-semibold text-gray-900 mb-4 text-base',
  };

  const contentStyles = {
    0: 'space-y-8',
    1: 'space-y-6 px-4',
    default: 'space-y-4 px-4',
  };

  return {
    HeadingTag: headingTags[Math.min(depth, headingTags.length - 1)],
    container:
      containerStyles[depth as keyof typeof containerStyles] ||
      containerStyles.default,
    section:
      sectionStyles[depth as keyof typeof sectionStyles] ||
      sectionStyles.default,
    heading:
      headingStyles[depth as keyof typeof headingStyles] ||
      headingStyles.default,
    content:
      contentStyles[depth as keyof typeof contentStyles] ||
      contentStyles.default,
  };
};

export const FormGroup = ({
  node,
  depth,
  disabled,
  onVisibilityChange,
}: FormGroupProps) => {
  const hasContent = node.questions.length > 0 || node.children.length > 0;
  const contentRef = useRef<HTMLDivElement>(null);
  const [hasVisibleContent, setHasVisibleContent] = useState(true);
  // Open/closed lives in ClarificationNavigationProvider so navigation can
  // expand a collapsed section; local state covers a FormGroup rendered alone.
  const disclosure = useFormDisclosure();
  const [localOpen, setLocalOpen] = useState(true);
  const open = disclosure ? disclosure.isSectionOpen(node.id) : localOpen;

  const toggleSectionOpen = () => {
    if (disclosure) disclosure.toggleSection(node.id);
    else setLocalOpen(!open);
  };

  // Watch all form values to trigger re-check when they change
  const formValues = useWatch();

  const subsectionStatus = useSubsectionStatus(node, formValues);
  const { submissionFormData } = useSubmissionFormContext();
  const isFillingIn = isFillingInStatus(submissionFormData?.status);

  // Check if the content div actually has any rendered children
  // This accounts for conditional questions that may all be hidden
  useEffect(() => {
    // Use requestAnimationFrame to ensure DOM is fully updated after React renders
    const checkVisibility = () => {
      if (contentRef.current) {
        // Only look for actual form input elements - this is the most reliable indicator
        // of whether there's actual interactive content in this section (including nested children)
        const hasFormElements = contentRef.current.querySelector(
          'input, select, textarea'
        );

        const visible = Boolean(hasFormElements);

        setHasVisibleContent(visible);

        // Notify parent component of visibility change for top-level sections (depth 0)
        if (depth === 0 && onVisibilityChange) {
          onVisibilityChange(node.id, visible);
        }
      }
    };

    const rafId = requestAnimationFrame(checkVisibility);
    return () => cancelAnimationFrame(rafId);
  }, [formValues, depth, node.id, onVisibilityChange]);

  // Additional check on mount to ensure section is registered immediately
  useEffect(() => {
    if (depth === 0 && onVisibilityChange && contentRef.current) {
      // Check if there are actual form elements in this section (including nested children)
      const hasFormElements = contentRef.current.querySelector(
        'input, select, textarea'
      );

      if (hasFormElements) {
        // Longer delay to ensure nested children are also rendered
        const timer = setTimeout(() => {
          onVisibilityChange(node.id, true);
        }, 200);
        return () => clearTimeout(timer);
      }
    }
  }, [depth, node.id, onVisibilityChange]);

  if (!hasContent) {
    return null;
  }

  const sectionId = `section-${node.id}`;
  const styles = getFormGroupStyles(depth);
  const { HeadingTag } = styles;

  // Render content invisibly to check if it has visible children
  const questionsContent = node.questions.length > 0 && (
    <div className="space-y-6">
      {node.questions.map(question => (
        <FormField key={question.id} question={question} disabled={disabled} />
      ))}
    </div>
  );

  const childrenContent = node.children.length > 0 && (
    <FormRenderer
      formTree={node.children}
      depth={depth + 1}
      disabled={disabled}
      onVisibilityChange={onVisibilityChange}
    />
  );

  // For depth 0, render heading inside the card with status badge and full-width divider
  if (depth === 0) {
    return (
      <div
        className={styles.container}
        style={!hasVisibleContent ? { display: 'none' } : {}}
        data-section-anchor={node.id}
      >
        <div className="flex items-center justify-between px-6 pt-6 pb-4">
          <HeadingTag className="font-semibold text-gray-900 text-2xl tracking-tight">
            {node.label}
          </HeadingTag>
          <div className="flex items-center gap-2 print:hidden">
            <SectionStatusBadge
              isFillingIn={isFillingIn}
              status={subsectionStatus.status}
              unresolvedCount={subsectionStatus.unresolvedCount}
              totalClarificationCount={subsectionStatus.totalClarificationCount}
            />
            {open ? (
              <ChevronDown onClick={toggleSectionOpen} />
            ) : (
              <ChevronUp onClick={toggleSectionOpen} />
            )}
          </div>
        </div>
        {open && (
          <div>
            <div className="border-b border-gray-200" />
            <section id={sectionId} className={styles.section} tabIndex={-1}>
              <div className={styles.content} ref={contentRef}>
                {questionsContent}
                {childrenContent}
              </div>
            </section>
          </div>
        )}
      </div>
    );
  }

  // For nested sections, keep heading inside
  return (
    <div
      className={styles.container}
      style={!hasVisibleContent ? { display: 'none' } : {}}
    >
      <section id={sectionId} className={styles.section} tabIndex={-1}>
        <HeadingTag className={styles.heading}>{node.label}</HeadingTag>

        <div className={styles.content} ref={contentRef}>
          {questionsContent}
          {childrenContent}
        </div>
      </section>
    </div>
  );
};
